/* eslint-disable class-methods-use-this */
import { service } from '@jutro/services';
import { LocaleSettingsInterface } from '@jutro/locale';
import { warning } from '@jutro/logger';
// eslint-disable-next-line import/no-unresolved
import LocaleConfig from 'locale-config';

export const LOCALE_SERVICE_ID = 'locale-service';
const LOCALE_REGEX = /^([a-z]{2,3})(?:[-_]([A-Z]{2}))?/;
const GW_LOCALE_REGEX = /^([x]{2}|[y]{2})$/;

function localeToIETF(locale) {
    return locale.replace(/_/g, '-');
}

function localeToPCNotation(locale) {
    const [, primaryLang, region] = LOCALE_REGEX.exec(locale);
    if (region) {
        return `${primaryLang}_${region}`;
    }
    if (primaryLang === 'en') {
        return 'en_US';
    }
    return primaryLang;
}

function localeToJutroNotation(locale) {
    const language = localeToIETF(locale);
    if (language === 'en-US') {
        return 'en';
    }
    if (language === 'es') {
        return 'es-ES';
    }
    return language;
}

class LocaleServiceImpl {
    defaultTimeZone = 'GMT';
    /**
     * Returns the available locales.
     * @returns {Array<string>} - The available locales
     */
    getAvailableLocales() {
        return Object.keys(LocaleConfig.locales).map(localeToIETF);
    }

    /**
     * Returns the available languages.
     * @returns {Array<string>} - The available languages
     */
    getAvailableLanguages() {
        return Object.keys(LocaleConfig.locales).map(localeToIETF);
    }

    /**
     * Gets the preferred locale.
     * @returns {string} - The preferred locale
     */
    getPreferredLocale() {
        return localeToIETF(LocaleConfig.preferredLocale);
    }

    getPreferredLanguage() {
        return localeToIETF(LocaleConfig.preferredLocale);
    }

    /**
     * Gets the default country code.
     * @returns {string} - The country code
     */
    getDefaultCountryCode() {
        return LocaleConfig.country.code;
    }

    /**
     * Gets the postal code regex.
     * @returns {string} - The postal code regex
     */
    getPostalCodeRegex() {
        return LocaleConfig.address.PostalCodeRegex;
    }

    /**
     * Returns the default phone country code.
     * @returns {String} - The default phone country code
     */
    getDefaultPhoneCountry() {
        return LocaleConfig.phone.UserDefaultPhoneCountry;
    }

    /**
     * Returns the default currency code.
     * @returns {String} - The default currency code
     */
    getDefaultCurrencyCode() {
        return LocaleConfig.currency.code;
    }

    /**
     * Returns the user's stored locale preference.
     * @returns {string | null}
     */
    getStoredLocale() {
        return localStorage.getItem('selectedLocale');
    }

    /**
     * Returns the user's stored language preference.
     * @returns {string | null}
     */
    getStoredLanguage() {
        return localStorage.getItem('selectedLanguage') || localeToIETF(LocaleConfig.preferredLocale);
    }

    /**
     * Stores the user's preferred locale.
     * @param {string} locale - The preferred locale
     */
    saveLocale(locale) {
        localStorage.setItem('selectedLocale', locale);
    }

    /**
     * Stores the user's preferred language.
     * @param {string} language - The preferred language
     */
    saveLanguage(language) {
        const activeLanguage = language || localeToIETF(LocaleConfig.preferredLocale);
        localStorage.setItem('selectedLanguage', language);
        // The portals don't have a specific locale chooser
        // so we change the locale when changing the language
        if (!GW_LOCALE_REGEX.test(activeLanguage)) {
            localStorage.setItem('selectedLocale', activeLanguage);
        }
        return activeLanguage;
    }

    static register() {
        service(LOCALE_SERVICE_ID, {
            implements: LocaleSettingsInterface
        })(LocaleServiceImpl);
    }

    /**
     * Load application specific messages
     * @param {string} lang
     * @returns {Promise<Object>} - Loaded messages
     * @static
     */
    static loadMessages = async (lang) => {
        const languageToLoad = localeToPCNotation(lang);
        try {
            const selectedLangMessages = await import(
                /* webpackInclude: /\.json$/ */
                /* webpackChunkName: "app-messages-[request]" */
                `app-translation/${languageToLoad}`
            );
            const defaultLang = LocaleConfig.preferredLocale;

            const defaultLangMessages = await import(
                /* webpackInclude: /\.json$/ */
                /* webpackChunkName: "app-messages-[request]" */
                `app-translation/${defaultLang}`
            );
            return Object.assign({}, defaultLangMessages, selectedLangMessages);
        } catch (error) {
            warning(`Unable to load translations for lang: ${lang}.`);
            return {};
        }
    };

    static loadCoreMessages = async (lang) => {
        const languageToLoad = localeToJutroNotation(lang);
        try {
            const result = await import(
                /* webpackInclude: /\.json$/ */
                /* webpackChunkName: "core-messages-[request]" */
                /* webpackMode: "eager" */
                `@jutro/translations/lang-data/${languageToLoad}.json`
            );
            return result;
        } catch (error) {
            warning(`Unable to load core translations for lang: ${lang}.`);
            return {};
        }
    };

    getDefaultTimeZone() {
        return this.defaultTimeZone;
    }
}

export default LocaleServiceImpl;
